import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';

// Governed enums + the program_definitions.config contract for the
// condition-agnostic program/clinical spine.
//
// Each constrained string domain is a single source of truth (ADR-056 Rule 8):
// the `const X = [...] as const` array drives BOTH the `text`+CHECK constraint
// rendered in the schema and the wire/contract schema here, so the DB and the
// validators cannot drift. This mirrors the ONBOARDING_STATUS pattern.
//
// `checkin_schema` is deliberately a free `z.string()`, not an enum: keeping it
// open is exactly what lets behavioral health land as data (a new check-in
// shape is a registry entry, not a code change).

// ─── program_definitions ──────────────────────────────────────────────────────
export const PROGRAM_DOMAIN = ['substance', 'behavioral', 'mood'] as const;
export const programDomainSchema = z.enum(PROGRAM_DOMAIN);
export type ProgramDomain = z.infer<typeof programDomainSchema>;

export const PROGRAM_STATUS = ['draft', 'active', 'retired'] as const;
export const programStatusSchema = z.enum(PROGRAM_STATUS);
export type ProgramStatus = z.infer<typeof programStatusSchema>;

export const PROGRAM_SOURCE = ['curated', 'agent_proposed'] as const;
export const programSourceSchema = z.enum(PROGRAM_SOURCE);
export type ProgramSource = z.infer<typeof programSourceSchema>;

// The lowest derived withdrawal risk that raises a safety escalation for a
// program's members, read from `config.withdrawal_escalation_min_risk`. Ordered
// low → high; `urgent` always raises whatever a program declares.
export const WITHDRAWAL_ESCALATION_MIN_RISK = ['positive', 'urgent'] as const;
export const withdrawalEscalationMinRiskSchema = z.enum(WITHDRAWAL_ESCALATION_MIN_RISK);
export type WithdrawalEscalationMinRisk = z.infer<typeof withdrawalEscalationMinRiskSchema>;

// ─── member_programs ──────────────────────────────────────────────────────────
export const MEMBER_PROGRAM_STATUS = [
  'planned',
  'active',
  'onhold',
  'finished',
  'cancelled',
] as const;
export const memberProgramStatusSchema = z.enum(MEMBER_PROGRAM_STATUS);
export type MemberProgramStatus = z.infer<typeof memberProgramStatusSchema>;

export const MEMBER_PROGRAM_OUTCOME = [
  'active',
  'resolved',
  'relapsed',
  'switched',
  'discharged',
] as const;
export const memberProgramOutcomeSchema = z.enum(MEMBER_PROGRAM_OUTCOME);
export type MemberProgramOutcome = z.infer<typeof memberProgramOutcomeSchema>;

// ─── care_pathways (clinical acuity tier) ───────────────────────────────────────
export const CARE_PATHWAY_TIER = ['support', 'manage', 'treat'] as const;
export const carePathwayTierSchema = z.enum(CARE_PATHWAY_TIER);
export type CarePathwayTier = z.infer<typeof carePathwayTierSchema>;

export const CARE_PATHWAY_SOURCE = ['screening', 'clinician', 'default'] as const;
export const carePathwaySourceSchema = z.enum(CARE_PATHWAY_SOURCE);
export type CarePathwaySource = z.infer<typeof carePathwaySourceSchema>;

// ─── care_plans (delivery mode) ─────────────────────────────────────────────────
// CareTrack is the delivery axis (ai_led | human_led) — distinct from the
// clinical acuity tier (support|manage|treat) carried on care_pathways. Named
// `CareTrack` (value + same-name type) to match the data-model §7.5 contract
// that consumers import.
export const CARE_TRACK = ['ai_led', 'human_led'] as const;
export const CareTrack = z.enum(CARE_TRACK);
export type CareTrack = z.infer<typeof CareTrack>;

// Who changed a member program's care track. `enrollment` is the care plan's
// creation, `staff` a care-team edit, `seed` the demo member-context seed, and
// `backfill` the one-off row for a care plan that predates the history.
export const CARE_TRACK_CHANGE_SOURCE = ['enrollment', 'staff', 'seed', 'backfill'] as const;
export const careTrackChangeSourceSchema = z.enum(CARE_TRACK_CHANGE_SOURCE);
export type CareTrackChangeSource = z.infer<typeof careTrackChangeSourceSchema>;

// ─── care_team roles (governed; = care_team_assignments.role when that lands) ───
export const CLINICIAN_ROLE = [
  'physician',
  'prescriber',
  'care_specialist',
  'counselor',
  'coordinator',
] as const;
export const ClinicianRole = z.enum(CLINICIAN_ROLE);
export type ClinicianRole = z.infer<typeof ClinicianRole>;

// ─── goals ──────────────────────────────────────────────────────────────────
export const GOAL_STATUS = ['active', 'achieved', 'abandoned'] as const;
export const goalStatusSchema = z.enum(GOAL_STATUS);
export type GoalStatus = z.infer<typeof goalStatusSchema>;

// ─── observations ─────────────────────────────────────────────────────────────
export const OBSERVATION_SOURCE = ['device', 'self_report', 'lab', 'clinician'] as const;
export const observationSourceSchema = z.enum(OBSERVATION_SOURCE);
export type ObservationSource = z.infer<typeof observationSourceSchema>;

// ─── activity_events authorship ───────────────────────────────────────────────
// Who authored a row: the member themself, or a care-team member on their
// behalf. `unknown` exists only for history whose author could not be proven
// from the data written alongside it; new writes record `member` or `clinician`.
export const ACTIVITY_AUTHOR_KIND = ['member', 'clinician', 'unknown'] as const;
export const activityAuthorKindSchema = z.enum(ACTIVITY_AUTHOR_KIND);
export type ActivityAuthorKind = z.infer<typeof activityAuthorKindSchema>;
/** The kinds a write records; `unknown` is backfill-only and never written live. */
export type RecordedActivityAuthorKind = Exclude<ActivityAuthorKind, 'unknown'>;

// ─── assessment_definitions ─────────────────────────────────────────────────────
export const ASSESSMENT_PURPOSE = ['intake', 'charting', 'in_program'] as const;
export const assessmentPurposeSchema = z.enum(ASSESSMENT_PURPOSE);
export type AssessmentPurpose = z.infer<typeof assessmentPurposeSchema>;

// ─── clients + member_eligibility (consumed read-model) ─────────────────────────
export const CLIENT_RELATIONSHIP_TYPE = ['employer', 'health_plan', 'partnership'] as const;
export const clientRelationshipTypeSchema = z.enum(CLIENT_RELATIONSHIP_TYPE);
export type ClientRelationshipType = z.infer<typeof clientRelationshipTypeSchema>;

export const ELIGIBILITY_RELATIONSHIP = ['employee', 'spouse', 'dependent'] as const;
export const eligibilityRelationshipSchema = z.enum(ELIGIBILITY_RELATIONSHIP);
export type EligibilityRelationship = z.infer<typeof eligibilityRelationshipSchema>;

export const ELIGIBILITY_STATUS = ['eligible', 'pending', 'ineligible'] as const;
export const eligibilityStatusSchema = z.enum(ELIGIBILITY_STATUS);
export type EligibilityStatus = z.infer<typeof eligibilityStatusSchema>;

// ─── program_definitions.config (the narrow waist) ──────────────────────────────
// Governed enums (closed) vs open refs (validated against their own catalogs
// server-side, never a DB CHECK — so a new instrument/checkin-schema/med is data,
// not a migration). Validated at the repo boundary (ADR-022) wherever a
// program_definitions row is read or written.

/**
 * One sub-type of a programme: its stored tag, the intake answers that mean it, and its label.
 * `prompt_label` is the name an agent prompt uses when the human-facing `label` alone would be
 * ambiguous out of context (next to other programmes, "Vaping" no longer says nicotine); readers
 * fall back to `label` when it is absent.
 */
export const SubtypeVocabularyEntry = z.object({
  tag: z.string().min(1),
  label: z.string().min(1),
  prompt_label: z.string().min(1).optional(),
  answer_values: z.array(z.string().min(1)),
  healthie_tag: z.string().min(1).optional(),
});
export type SubtypeVocabularyEntry = z.infer<typeof SubtypeVocabularyEntry>;

/**
 * A programme's sub-types, the intake answer key that selects one, and the label when none was given.
 *
 * Deliberately NOT `.strict()`, here or on each entry. This object reaches installed mobile builds
 * inside `config` on `GET /members/me/substance-config`, and `ProgramConfigRead` strips unknown keys
 * only at the top level, so a strict nested object would make every installed build reject the
 * response the day a field is added inside it (ADR-135 Rule 1). Unknown nested keys are stripped on
 * read; the seed's exact shape is pinned by its own test instead.
 */
export const SubtypeVocabulary = z.object({
  answer_key: z.string().min(1),
  unspecified_label: z.string().min(1),
  entries: z.array(SubtypeVocabularyEntry).min(1),
});
export type SubtypeVocabulary = z.infer<typeof SubtypeVocabulary>;

export const ProgramConfig = z
  .object({
    instruments: z.array(z.string()), // → assessment_definitions.key
    journey_template_ref: z.string(), // → S3-served journey content
    checkin_schema: z.string(), // → check-in-schema registry key (open ref, NOT enum)
    // Expand/contract compatibility: the generic form is the canonical
    // capability and the only form the catalog seed writes. The legacy form
    // stays readable for rows written before the seed moved off it; the arm is
    // removed once no stored row in any environment carries it.
    initial_review: z
      .union([
        z
          .object({
            presentation_id: z.literal('quantified_check_ins_v1'),
            quantity_limit: z.number().int().positive(),
            // Open ref into the locale catalog's `initial_review.presentation.<copy_ref>`
            // copy groups (validated server-side against the catalog, like
            // `checkin_schema`), so a program's unit and free-day wording is data.
            copy_ref: z.string().min(1),
          })
          .strict(),
        z
          .object({
            presentation_id: z.literal('initial_alcohol_check_ins_v1'),
            quantity_limit: z.number().int().positive(),
            weekly_target_quantity: z.number().int().nonnegative(),
          })
          .strict(),
      ])
      .transform((configuration) =>
        configuration.presentation_id === 'quantified_check_ins_v1'
          ? configuration
          : {
              presentation_id: 'quantified_check_ins_v1' as const,
              quantity_limit: configuration.quantity_limit,
              copy_ref: 'quantified_substance_v1' as const,
            },
      )
      .optional(),
    meds_in_scope: z.array(z.string()), // → RxNorm / medication catalog ref
    vitals_of_interest: z.array(z.string()), // → observations.code
    healthie_tag: z.string(), // EHR mapping
    clinician_roles: z.array(ClinicianRole),
    care_track_rules: z
      .object({
        default: CareTrack,
        escalate_if: z.array(z.string()),
      })
      .strict(),
    // Clinical priority for main-program selection when a member enrolls in
    // several programs (lower = higher priority); a data-driven ordering so the
    // primary is never a hardcoded key switch (ADR-015). Absent → lowest priority.
    priority: z.number().int().nonnegative().optional(),
    // Minimum registration age for this program, in whole years. Catalog data
    // so a compliance ruling is a seed change, never code. Absent → no floor
    // (which disables the global under-min-age gate for everyone, since that
    // gate requires being under EVERY program's floor).
    min_age: z.number().int().positive().optional(),
    // Allowed sub-type tags for this program (e.g. tobacco → cigarettes /
    // ecigarettes / smokeless-tobacco). A server-side validation catalog for the
    // `member_program_tags` write — NOT a join surface. Absent → no sub-types.
    intake: z
      .object({ subtypes: z.array(z.string()) })
      .strict()
      .optional(),
    // The programme's sub-type vocabulary: for each sub-type tag, the intake
    // answer values that map to it, the one label every reader shows, and an
    // optional EHR tag name. The single source for enrollment, prompt context,
    // staff surfaces and Healthie sync, so no reader keeps its own spelling.
    // A top-level key on purpose, not a field inside `intake`: `intake` is
    // `.strict()` and `ProgramConfigRead` only strips unknown TOP-LEVEL keys, so
    // the release still serving traffic while this row lands keeps parsing it
    // (ADR-056 expand/contract). Absent → the programme has no sub-types.
    subtype_vocabulary: SubtypeVocabulary.optional(),
    // The countable noun for the member's logged week (e.g. "drinks",
    // "cigarettes"), resolved server-side so a staff surface renders whatever
    // arrives instead of mapping a program key to a unit itself (ADR-015
    // Rule 2/5 — a new condition ships as catalog data, not a client release).
    // Optional: a program that declares none renders unitless numbers rather
    // than borrowing another program's unit.
    usage_unit: z.string().min(1).optional(),
    // Whether a member who declines the Sona conversation is asked the
    // structured physical-dependence (withdrawal) check at the head of the
    // manual onboarding path. Catalog data so the clinical population is a
    // seed edit, never a client release (ADR-015 Rule 3/5): a ruling that a
    // program should stop (or start) asking lands as config on every installed
    // build. Absent → the program asks no withdrawal check.
    withdrawal_check: z.boolean().optional(),
    // Which withdrawal answers on that check raise the intake safety
    // escalation for this program: `positive` (any symptom) or `urgent` (only
    // seizures, hallucinations or confusion). Catalog data so a clinical ruling
    // on a program's population is a config change, never a program-key branch
    // (ADR-015 Rule 3/5). Absent → `positive`, so a missing value can only
    // over-escalate. Top-level on purpose: `ProgramConfigRead` strips unknown
    // top-level keys only, so the previous release keeps parsing this row.
    // `care_track_rules.escalate_if` does not govern this escalation.
    withdrawal_escalation_min_risk: withdrawalEscalationMinRiskSchema.optional(),
    // TAPS 2 screening set for this program: the flow-collected question keys
    // (flat onboarding answer keys) and the answer values that resolve a
    // positive SUD screening signal. Server-side data so a new substance's set
    // lands as catalog config, never app-code logic. Absent → the program asks
    // no TAPS 2 questions and derives no signal.
    taps2: z
      .object({
        questions: z
          .array(
            z
              .object({
                key: z.string().min(1),
                positive_values: z.array(z.string()).min(1),
              })
              .strict(),
          )
          .min(1),
      })
      .strict()
      .optional(),
    // Care scheduling derivation inputs (ADR-015): programme data drives which
    // welcome call and substance-readiness gate apply — never a condition literal.
    care: z
      .object({
        welcome_call_type: z
          .enum([
            'welcome_call_substance',
            'welcome_call_tobacco',
            'welcome_call_behavioral',
            'welcome_call_substance',
          ])
          .default('welcome_call_substance'),
        licence_readiness: z.enum(['aud', 'oud']).nullable().default(null),
        medication_eligible: z.boolean().default(false),
        followed_by_treatment_plan: z.boolean().optional(),
      })
      .strict()
      .optional(),
  })
  .strict(); // exact field set — reject unknown keys at the repo boundary, not silently strip
export type ProgramConfig = z.infer<typeof ProgramConfig>;

/**
 * Read-side variant for LIVE parse sites (repos reading `program_definitions`
 * rows): identical fields, but unknown TOP-LEVEL keys are stripped instead of
 * rejected. `.strip()` does not recurse — the nested `care_track_rules`, `intake`
 * and `taps2` objects keep their own `.strict()`, so an additive field inside one
 * of those still fails a reader on the old shape.
 * Seeds and writes stay on the `.strict()` schema so an authoring typo still
 * fails loudly; readers must tolerate a NEWER row shape, because the seed job
 * runs before the Lambda deploy and the previous release's code reads the new
 * data for the whole deploy window (ADR-056 expand/contract).
 */
export const ProgramConfigRead = ProgramConfig.strip();
export type ProgramConfigInput = z.input<typeof ProgramConfig>;

/**
 * Which path wrote a `member_programs` row, as `program.enrolled` reports it.
 * `onboarding_completion` is the intake finalize and its consumer; the other
 * three name paths that write no live row today — the migration gap flow, the
 * migration import (which publishes nothing), and any other writer — and are
 * declared so a writer that starts using one does not change the vocabulary
 * under the charts.
 */
export const ENROLLMENT_SOURCES = [
  'onboarding_completion',
  'migration_gap_flow',
  'migration',
  'other',
] as const;
export const enrollmentSourceSchema = z.enum(ENROLLMENT_SOURCES);
export type EnrollmentSource = z.infer<typeof enrollmentSourceSchema>;

const programCountSchema = z.number().int().nonnegative();

// The `program.enrolled` domain-event payload (ADR-052 `data`): ids + program
// domain + whether it's the member's main program, and which path wrote the
// row, whether the member came from Pelago 1.0, and their active program count
// either side of it. PHI-safe — no answer values, scores, or tags. The four
// later fields are optional so an envelope queued before they existed still
// parses, and nullable because the writer reports null when it could not read
// them.
export const programEnrolledDataSchema = z
  .object({
    programDefinitionId: z.string().uuid(),
    memberProgramId: z.string().uuid(),
    domain: programDomainSchema,
    mainProgram: z.boolean(),
    enrollmentSource: enrollmentSourceSchema.optional(),
    memberMigrated: z.boolean().nullable().optional(),
    programCountBefore: programCountSchema.nullable().optional(),
    programCountAfter: programCountSchema.nullable().optional(),
    // Added to `data` by the annotated-event builder; present on the wire.
    status: z.literal('created').optional(),
  })
  .strict();
export type ProgramEnrolledData = z.infer<typeof programEnrolledDataSchema>;

// ─── ending an episode ──────────────────────────────────────────────────────────
// Why an episode ended, in Pelago 1.0's "termed" vocabulary so client files and
// lifecycle reporting keep one set of reasons across both platforms.
export const PROGRAM_END_REASON = [
  'met_goals',
  'higher_level_of_care',
  'voluntary_discontinuation',
  'not_eligible',
  'other',
] as const;
export const programEndReasonSchema = z.enum(PROGRAM_END_REASON);
export type ProgramEndReason = z.infer<typeof programEndReasonSchema>;

export const PROGRAM_END_SOURCE_KIND = ['healthie_pact', 'atlas_staff'] as const;
export const programEndSourceKindSchema = z.enum(PROGRAM_END_SOURCE_KIND);
export type ProgramEndSourceKind = z.infer<typeof programEndSourceKindSchema>;

// Who ended the episode. The id is the idempotency key: a repeat call from the
// same form answer or staff member is a no-op rather than a second end.
export const programEndSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('healthie_pact'), formAnswerId: z.string().trim().min(1) }).strict(),
  z.object({ kind: z.literal('atlas_staff'), internalUserId: z.string().uuid() }).strict(),
]);
export type ProgramEndSource = z.infer<typeof programEndSourceSchema>;

export const PROGRAM_END_NOTE_MAX_LENGTH = 1000;
const programEndNoteSchema = z.string().max(PROGRAM_END_NOTE_MAX_LENGTH);

// An end that is not "still active": the writer never leaves `outcome = 'active'`
// on a finished row, which is what keeps outcome-keyed readers correct.
export const programEndOutcomeSchema = memberProgramOutcomeSchema.exclude(['active']);
export type ProgramEndOutcome = z.infer<typeof programEndOutcomeSchema>;

const requireNoteForOther = (
  value: { reason: ProgramEndReason; note?: string | null },
  ctx: z.RefinementCtx,
): void => {
  if (value.reason === 'other' && (value.note ?? '').trim().length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['note'],
      message: 'a note is required when the end reason is other',
    });
  }
};

// One entry in `member_programs.status_history`. The note may carry clinical
// detail, so this column is the only place it is stored.
export const memberProgramStatusHistoryEntrySchema = z
  .object({
    at: z.string().datetime({ offset: true }),
    fromStatus: memberProgramStatusSchema,
    toStatus: memberProgramStatusSchema,
    outcome: memberProgramOutcomeSchema,
    reason: programEndReasonSchema,
    note: programEndNoteSchema.nullable(),
    source: programEndSourceSchema,
  })
  .strict()
  .superRefine(requireNoteForOther);
export type MemberProgramStatusHistoryEntry = z.infer<typeof memberProgramStatusHistoryEntrySchema>;

// Input to the internal `endProgram` writer. `endedAt` defaults to now; the
// not-in-the-future rule is checked by the writer, which owns the clock.
export const endProgramInputSchema = z
  .object({
    memberId: memberIdSchema,
    memberProgramId: z.string().uuid(),
    outcome: programEndOutcomeSchema,
    reason: programEndReasonSchema,
    note: programEndNoteSchema.optional(),
    endedAt: z.string().datetime({ offset: true }).optional(),
    source: programEndSourceSchema,
  })
  .strict()
  .superRefine(requireNoteForOther);
export type EndProgramInput = z.infer<typeof endProgramInputSchema>;

// The `program.ended` domain-event payload. Carries the reason and the kind of
// source but never the note or the source's id.
export const programEndedDataSchema = z
  .object({
    programDefinitionId: z.string().uuid(),
    memberProgramId: z.string().uuid(),
    domain: programDomainSchema,
    mainProgram: z.boolean(),
    outcome: programEndOutcomeSchema,
    endReason: programEndReasonSchema,
    endSourceKind: programEndSourceKindSchema,
    endedAt: z.string().datetime({ offset: true }),
    // Added to `data` by the annotated-event builder; present on the wire.
    status: z.literal('ended').optional(),
  })
  .strict();
export type ProgramEndedData = z.infer<typeof programEndedDataSchema>;
